import React from "react";
import styled from "styled-components";
import { Helmet } from "react-helmet";

const Privacy = () => {
  return (
    <>
      <Helmet>
        <title>AgroHarvest | Privacy Policy</title>
        <meta
          name="description"
          content="Read AgroHarvest Machineries' privacy policy to understand how we collect, use, and protect your personal information."
        />
      </Helmet>
      <ThePrivacy>

        <div className="page-hero">
          <div className="hero-inner">
            <span className="hero-label">AgroHarvest Machineries — Legal</span>
            <h1>Privacy<br />Policy</h1>
            <p className="hero-sub">Your privacy matters to us.</p>
          </div>
          <div className="hero-rule" />
        </div>

        <div className="page-container">

          <div className="meta-row">
            <span className="section-tag">Effective Date</span>
            <span className="meta-date">October 20, 2017</span>
          </div>

          <div className="content-grid">

            <div className="sections-col">

              <div className="policy-section">
                <span className="section-tag">01</span>
                <h2>Our Commitment to Privacy</h2>
                <p>
                  Your privacy is important to us. It is our policy to respect your privacy
                  regarding any information we may collect from you across our website,{" "}
                  <a href="https://www.agroharvestmachineries.com">
                    agroharvestmachineries.com
                  </a>
                  , and other sites we own and operate.
                </p>
              </div>

              <div className="policy-section">
                <span className="section-tag">02</span>
                <h2>Information Collection</h2>
                <p>
                  We only ask for personal information when we truly need it to provide a
                  service to you. We collect it by fair and lawful means, with your knowledge
                  and consent. We also let you know why we're collecting it and how it will be used.
                </p>
              </div>

              <div className="policy-section">
                <span className="section-tag">03</span>
                <h2>Data Retention</h2>
                <p>
                  We only retain collected information for as long as necessary to provide
                  you with your requested service. What data we store, we'll protect within
                  commercially acceptable means to prevent loss and theft, as well as
                  unauthorized access, disclosure, copying, use or modification.
                </p>
              </div>

              <div className="policy-section">
                <span className="section-tag">04</span>
                <h2>Information Sharing</h2>
                <p>
                  We don't share any personally identifying information publicly or with
                  third-parties, except when required to by law. Our website may link to
                  external sites that are not operated by us. Please be aware that we have
                  no control over the content and practices of these sites, and cannot
                  accept responsibility or liability for their respective privacy policies.
                </p>
              </div>

              <div className="policy-section">
                <span className="section-tag">05</span>
                <h2>Your Rights</h2>
                <p>
                  You are free to refuse our request for your personal information, with
                  the understanding that we may be unable to provide you with some of your
                  desired services.
                </p>
              </div>

              <div className="policy-section">
                <span className="section-tag">06</span>
                <h2>Consent</h2>
                <p>
                  Your continued use of our website will be regarded as acceptance of our
                  practices around privacy and personal information. If you have any
                  questions about how we handle user data and personal information, feel
                  free to <a href="/contact-us">contact us</a>.
                </p>
              </div>

              <div className="policy-section">
                <span className="section-tag">07</span>
                <h2>Policy Updates</h2>
                <p>
                  This policy is effective as of 20th October 2017. We may update this
                  privacy policy from time to time. Any changes will be posted on this
                  page with an updated effective date.
                </p>
              </div>

              <div className="policy-section partner-section">
                <span className="section-tag">08</span>
                <h2>Third-Party Partners</h2>
                <p>Privacy Policy of AgroHarvest Machineries represents:</p>
                <div className="partner-notice">
                  <span className="partner-name">CNH Industrial</span>
                  <p>
                    By making a purchase, you acknowledge and agree to CNH Industrial's
                    privacy practices as described in their{" "}
                    <a
                      href="https://www.mycnhistore.com/us/en/default/privacynotice"
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      Privacy Notice
                    </a>
                    .
                  </p>
                </div>
              </div>

            </div>

            <div className="sidebar">
              <div className="contact-block">
                <span className="section-tag">Questions?</span>
                <h3>Privacy Inquiries</h3>
                <p>
                  If you have any questions about this Privacy Policy or how we handle
                  your data, please contact us.
                </p>
                <div className="contact-links">
                  <a href="mailto:privacy@agroharvestmachineries.com">
                    privacy@agroharvestmachineries.com
                  </a>
                  <a href="tel:+12706320751"> 1-270-632-0751</a>
                </div>
              </div>
            </div>

          </div>
        </div>

      </ThePrivacy>
    </>
  );
};

const ThePrivacy = styled.div`
  width: 100%;
  min-height: 100vh;
  background: #f5f2ed;
  font-family: 'Georgia', 'Times New Roman', serif;
  color: #1c1c1c;

  /* ── HERO ── */
  .page-hero {
    background: var(--primary-color);
    padding: 7rem 6rem 4rem;

    .hero-inner {
      max-width: 900px;

      .hero-label {
        display: block;
        font-family: 'Courier New', monospace;
        font-size: 0.75rem;
        letter-spacing: 0.18em;
        text-transform: uppercase;
        color: rgba(255,255,255,0.45);
        margin-bottom: 2rem;
      }

      h1 {
        font-size: clamp(3rem, 8vw, 6rem);
        font-weight: 400;
        color: #f5f2ed;
        line-height: 1.0;
        letter-spacing: -0.02em;
        margin: 0 0 1.8rem;
      }

      .hero-sub {
        font-size: 1rem;
        color: rgba(255,255,255,0.5);
        font-style: italic;
        margin: 0;
      }
    }

    .hero-rule {
      width: 100%;
      height: 1px;
      background: rgba(255,255,255,0.15);
      margin-top: 4rem;
    }
  }

  /* ── CONTAINER ── */
  .page-container {
    max-width: 1100px;
    margin: 0 auto;
    padding: 4rem 4rem 6rem;
  }

  .section-tag {
    display: inline-block;
    font-family: 'Courier New', monospace;
    font-size: 0.7rem;
    letter-spacing: 0.2em;
    text-transform: uppercase;
    color: #7a7265;
    margin-bottom: 0.6rem;
  }

  /* ── META ROW ── */
  .meta-row {
    display: flex;
    align-items: center;
    gap: 1rem;
    padding-bottom: 2.5rem;
    border-bottom: 1px solid #d5d0c8;
    margin-bottom: 3.5rem;

    .section-tag { margin-bottom: 0; }

    .meta-date {
      font-family: 'Courier New', monospace;
      font-size: 0.78rem;
      letter-spacing: 0.1em;
      color: #3d3d3d;
    }
  }

  /* ── CONTENT GRID ── */
  .content-grid {
    display: grid;
    grid-template-columns: 1fr 280px;
    gap: 6rem;
    align-items: start;
  }

  /* ── POLICY SECTIONS ── */
  .sections-col {
    display: flex;
    flex-direction: column;

    .policy-section {
      padding: 2rem 0;
      border-bottom: 1px solid #d5d0c8;

      &:first-child { padding-top: 0; }
      &:last-child { border-bottom: none; }

      h2 {
        font-size: 1.25rem;
        font-weight: 400;
        color: var(--primary-color);
        margin: 0 0 1rem;
        line-height: 1.3;
      }

      p {
        font-size: 0.97rem;
        line-height: 1.82;
        color: #3d3d3d;
        margin: 0;

        & + p { margin-top: 0.8rem; }
      }

      a {
        color: var(--primary-color);
        text-decoration: underline;
        text-underline-offset: 3px;
        transition: opacity 0.2s;

        &:hover { opacity: 0.7; }
      }
    }

    .partner-section {
      .partner-notice {
        margin-top: 1.2rem;
        border-left: 2px solid #d5d0c8;
        padding-left: 1.2rem;

        .partner-name {
          display: block;
          font-family: 'Courier New', monospace;
          font-size: 0.72rem;
          letter-spacing: 0.14em;
          text-transform: uppercase;
          color: var(--primary-color);
          margin-bottom: 0.5rem;
        }

        p {
          font-size: 0.92rem;
          line-height: 1.7;
          color: #555;
          margin: 0;
        }
      }
    }
  }

  /* ── SIDEBAR ── */
  .sidebar {
    position: sticky;
    top: 2rem;

    .contact-block {
      border: 1px solid #d5d0c8;
      padding: 1.8rem;
      background: #fff;

      h3 {
        font-size: 1.1rem;
        font-weight: 400;
        color: var(--primary-color);
        margin: 0 0 0.7rem;
      }

      p {
        font-size: 0.88rem;
        line-height: 1.7;
        color: #666;
        margin: 0 0 1.5rem;
        font-style: italic;
        border-bottom: 1px solid #d5d0c8;
        padding-bottom: 1.5rem;
      }

      .contact-links {
        display: flex;
        flex-direction: column;
        gap: 0.7rem;

        a {
          font-family: 'Courier New', monospace;
          font-size: 0.75rem;
          letter-spacing: 0.05em;
          color: var(--primary-color);
          text-decoration: none;
          transition: opacity 0.2s;

          &:hover { opacity: 0.65; }
        }
      }
    }
  }

  /* ── RESPONSIVE ── */
  @media screen and (max-width: 1024px) {
    .page-hero { padding: 5rem 3rem 3rem; }
    .page-container { padding: 3.5rem 3rem 5rem; }
    .content-grid { gap: 4rem; grid-template-columns: 1fr 240px; }
  }

  @media screen and (max-width: 768px) {
    .page-hero {
      padding: 4rem 2rem 2.5rem;
      h1 { font-size: 3rem; }
    }
    .page-container { padding: 3rem 2rem 4rem; }
    .content-grid {
      grid-template-columns: 1fr;
      gap: 0;
    }
    .sidebar {
      position: static;
      margin-top: 3rem;
      padding-top: 3rem;
      border-top: 1px solid #d5d0c8;
    }
  }

  @media screen and (max-width: 600px) {
    .page-hero {
      padding: 3rem 1.5rem 2rem;
      h1 { font-size: 2.4rem; }
      .hero-rule { margin-top: 2.5rem; }
    }
    .page-container { padding: 2.5rem 1.5rem 3rem; }
  }

  @media screen and (max-width: 420px) {
    .page-hero {
      padding: 2.5rem 1.2rem 1.8rem;
      h1 { font-size: 2rem; }
    }
    .page-container { padding: 2rem 1.2rem 2.5rem; }
  }

  @media screen and (max-width: 350px) {
    .page-hero h1 { font-size: 1.7rem; }
  }
`;

export default Privacy;